import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  InputAdornment,
  IconButton,
  Container,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Login as LoginIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { login, login2FA, clearError, cancelTwoFactor } from '../../store/slices/authSlice';

const validationSchema = Yup.object({
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  password: Yup.string()
    .min(6, 'Password must be at least 6 characters')
    .required('Password is required'),
});

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { isLoading, error, twoFactorChallengeToken } = useSelector((state: RootState) => state.auth);

  const [showPassword, setShowPassword] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const from = location.state?.from?.pathname || '/dashboard';

  // Clear any stale errors when navigating to the login page
  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const result = await dispatch(login(values)).unwrap();
        if (!result.requires2FA) {
          navigate(from, { replace: true });
        }
      } catch (error) {
        // Error handled by Redux
      }
    },
  });

  const handleSubmit2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!twoFactorChallengeToken || !/^\d{6}$/.test(twoFactorCode)) return;
    try {
      await dispatch(login2FA({ challengeToken: twoFactorChallengeToken, code: twoFactorCode })).unwrap();
      navigate(from, { replace: true });
    } catch (error) {
      // Error handled by Redux
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      background: 'linear-gradient(135deg, #F8F8FC 0%, #E8F4FD 100%)',
    }}>
      {/* Left decorative panel - hidden on mobile */}
      <Box sx={{
        display: { xs: 'none', md: 'flex' },
        flex: 1,
        background: 'linear-gradient(135deg, #09486D 0%, #11C1EC 100%)',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        p: 6,
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Decorative circles */}
        <Box sx={{ position: 'absolute', top: -60, left: -60, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
        <Box sx={{ position: 'absolute', bottom: -80, right: -40, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.03)' }} />
        <Box sx={{ position: 'absolute', top: '40%', right: -20, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />

        <Box
          component="img"
          src="https://mal-pay.com/images/logo22.png"
          alt="MalPay"
          sx={{ height: 60, mb: 3, filter: 'brightness(10)' }}
          onError={(e: any) => { e.target.style.display = 'none'; }}
        />
        <Typography variant="h3" sx={{ color: 'white', fontWeight: 800, mb: 2, textAlign: 'center' }}>
          Pay online with ease.
        </Typography>
        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)', textAlign: 'center', maxWidth: 400, lineHeight: 1.8 }}>
          Complete online transactions at your convenience, fast, easy and secured.
        </Typography>
      </Box>

      {/* Right login form */}
      <Box sx={{
        flex: { xs: 1, md: '0 0 520px' },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        p: { xs: 3, sm: 6 },
      }}>
        <Container maxWidth="sm" sx={{ width: '100%', maxWidth: 420 }}>
          {/* Logo for mobile */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', mb: 4 }}>
            <Box
              component="img"
              src="https://mal-pay.com/images/logo22.png"
              alt="MalPay"
              sx={{ height: 50 }}
              onError={(e: any) => { e.target.style.display = 'none'; }}
            />
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#202647', mb: 1 }}>
              Welcome back
            </Typography>
            <Typography variant="body1" sx={{ color: '#7F839C' }}>
              Sign in to continue to your MalPay account
            </Typography>
          </Box>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3, borderRadius: 3 }}
              onClose={() => dispatch(clearError())}
            >
              {error}
            </Alert>
          )}
          {twoFactorChallengeToken ? (
            <form onSubmit={handleSubmit2FA}>
              <Typography variant="body2" sx={{ color: '#7F839C', mb: 2 }}>
                Enter the 6-digit code from your authenticator app.
              </Typography>
              <TextField
                fullWidth
                autoFocus
                id="twoFactorCode"
                name="twoFactorCode"
                placeholder="123456"
                inputProps={{ inputMode: 'numeric', maxLength: 6, style: { letterSpacing: '0.4em', textAlign: 'center', fontSize: '1.4rem' } }}
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                sx={{ mb: 3 }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading || twoFactorCode.length !== 6}
                sx={{ py: 1.5, fontSize: '1rem', fontWeight: 700, borderRadius: 3,
                  background: 'linear-gradient(135deg, #11C1EC 0%, #09486D 100%)' }}
              >
                {isLoading ? 'Verifying...' : 'Verify code'}
              </Button>
              <Button fullWidth sx={{ mt: 1.5 }} onClick={() => { setTwoFactorCode(''); dispatch(cancelTwoFactor()); }}>
                Back to sign in
              </Button>
            </form>
          ) : (


          <form onSubmit={formik.handleSubmit}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#272A3A', mb: 0.5 }}>
              Email Address
            </Typography>
            <TextField
              fullWidth
              id="email"
              name="email"
              placeholder="you@example.com"
              type="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: '#B0B0C0' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2.5 }}
            />

            <Typography variant="body2" sx={{ fontWeight: 600, color: '#272A3A', mb: 0.5 }}>
              Password
            </Typography>
            <TextField
              fullWidth
              id="password"
              name="password"
              placeholder="Enter your password"
              type={showPassword ? 'text' : 'password'}
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: '#B0B0C0' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff sx={{ color: '#B0B0C0' }} /> : <Visibility sx={{ color: '#B0B0C0' }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              startIcon={!isLoading && <LoginIcon />}
              sx={{
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 700,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #11C1EC 0%, #09486D 100%)',
                boxShadow: '0 4px 15px rgba(17, 193, 236, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0EAED6 0%, #07395A 100%)',
                  boxShadow: '0 6px 20px rgba(17, 193, 236, 0.4)',
                },
              }}
            >
              {isLoading ? 'Signing In...' : 'Sign In'}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Typography variant="body2" align="center" sx={{ mb: 1 }}>
                <Link component={RouterLink} to="/forgot-password">Forgot password?</Link>
              </Typography>
              <Typography variant="body2" sx={{ color: '#7F839C' }}>
                Don't have an account?{' '}
                <Link
                  component={RouterLink}
                  to="/register"
                  variant="body2"
                  sx={{ color: '#11C1EC', fontWeight: 700, textDecoration: 'none', '&:hover': { color: '#09486D' } }}
                >
                  Sign up here
                </Link>
              </Typography>
            </Box>
          </form>
          )}

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#B0B0C0', fontSize: '0.8rem' }}>
              Secure payments powered by Paystack
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LoginPage;
